import { ArrowBack, CheckCircle, ContentCopy } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { usePaymentPageRules } from './PaymentPage.rules';

const PaymentPage: React.FC = () => {
  const { transacaoId } = useParams<{ transacaoId: string }>();
  const navigate = useNavigate();
  const { transacao, pixInfo, loading, error } = usePaymentPageRules(transacaoId);
  const [copiado, setCopiado] = useState(false);

  const isDemo = transacao?.lista.slug === 'teste';
  const demoPixInfo = {
    copiaECola:
      '00020126360014BR.GOV.BCB.PIX0114+5511999999995204000053039865407779.005802BR5925LISTA DEMO - NÃO PAGAR6009SAO PAULO62070503***6304ABCD',
  };
  const pixParaMostrar = isDemo ? demoPixInfo : pixInfo;
  const qrCodeValue = pixParaMostrar?.copiaECola ?? '';
  const demoPixImage = `${process.env.PUBLIC_URL || ''}/pix-demo-exemplo.jpg`;

  const copiarCodigoPix = async () => {
    if (pixParaMostrar?.copiaECola) {
      try {
        await navigator.clipboard.writeText(pixParaMostrar.copiaECola);
        setCopiado(true);
        setTimeout(() => setCopiado(false), 2000);
      } catch (err) {
        console.error('Erro ao copiar:', err);
      }
    }
  };

  const formatarValor = (valor: number) => {
    return valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  const formatarTempo = (data: string) => {
    return new Date(data).toLocaleString('pt-BR');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(`/lista/${transacao?.lista.slug}`)}
        sx={{ mb: 3 }}
      >
        Voltar à lista
      </Button>

      {transacao?.status === 'pago' ? (
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: 'success.light',
            color: 'success.contrastText',
          }}
        >
          <CheckCircle sx={{ fontSize: 80, mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Pagamento Confirmado!
          </Typography>
          <Typography variant="h6" paragraph>
            Obrigado, {transacao.presenteadorNome}!
          </Typography>
          <Typography variant="body1">
            Seu presente "{transacao.present.nome}" foi confirmado na lista{' '}
            {transacao.lista.tituloLista}.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Quem criou a lista receberá uma notificação sobre seu presente.
          </Typography>
        </Paper>
      ) : (
        <Box>
          <Typography variant="h4" gutterBottom textAlign="center">
            Finalize seu Presente
          </Typography>

          {isDemo && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Modo demonstração:</strong> exemplo de fluxo PIX com dados fictícios. Ao ler o
              QR, o app do banco já abre com o pagamento preenchido.
            </Alert>
          )}

          <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={4}>
            {/* QR Code PIX */}
            <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h5" gutterBottom>
                {isDemo ? 'Exemplo de pagamento' : 'Pague com PIX'}
              </Typography>

              {transacao?.lista.isOwner && !pixParaMostrar && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    border: '1.5px dashed',
                    borderColor: 'warning.main',
                    borderRadius: 3,
                    bgcolor: 'warning.lighter',
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ color: 'warning.dark' }}>
                    ⚠️ Você ainda não cadastrou sua chave PIX
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Sem a chave PIX, os convidados não conseguem gerar o QR Code para presentear
                    você. Cadastre agora para não perder nenhum presente.
                  </Typography>
                  <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={() => navigate(`/${transacao.lista.slug}?openEdit=1`)}
                    >
                      Editar lista e cadastrar PIX
                    </Button>
                    <Button variant="outlined" onClick={() => navigate(`/${transacao.lista.slug}`)}>
                      Ver minha lista
                    </Button>
                  </Stack>
                </Paper>
              )}

              {pixParaMostrar && (
                <Box>
                  <Box display="flex" justifyContent="center" mb={3}>
                    {qrCodeValue ? (
                      <QRCodeSVG value={qrCodeValue} size={200} level="H" includeMargin />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        QR Code indisponível para esta transação.
                      </Typography>
                    )}
                  </Box>

                  <Typography variant="body2" color="text.secondary" paragraph>
                    {isDemo
                      ? 'Após ler o QR Code, o convidado abre o app do banco com os dados do PIX preenchidos. Também pode copiar o código abaixo.'
                      : 'Escaneie o QR Code: seu app do banco abre com o PIX já preenchido. Se preferir, copie o código abaixo.'}
                  </Typography>

                  <Box textAlign="left" mb={1}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      Código PIX (copia e cola)
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography
                        variant="body2"
                        sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}
                      >
                        {pixParaMostrar.copiaECola}
                      </Typography>
                    </Paper>
                  </Box>

                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ContentCopy />}
                    onClick={copiarCodigoPix}
                    sx={{ mb: 2 }}
                    disabled={!pixParaMostrar.copiaECola}
                  >
                    {copiado ? 'Código Copiado!' : 'Copiar Código PIX'}
                  </Button>

                  <Typography variant="caption" color="text.secondary">
                    {isDemo
                      ? 'Fluxo ilustrativo com dados fictícios.'
                      : 'Escaneando, o app já leva ao pagamento com dados preenchidos; o código também funciona para copiar e colar.'}
                  </Typography>
                </Box>
              )}

              {isDemo && (
                <Box mt={3}>
                  <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.50' }}>
                    <img
                      src={demoPixImage}
                      alt="Exemplo de comprovante PIX"
                      style={{ width: '100%', borderRadius: 8 }}
                    />
                  </Paper>
                  <Typography variant="caption" color="text.secondary">
                    Exemplo ilustrativo do comprovante (demonstração).
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Detalhes da Transação */}
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Resumo do Presente
                </Typography>

                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Para:
                  </Typography>
                  <Typography variant="h6">{transacao?.lista.tituloLista}</Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Presente:
                  </Typography>
                  <Typography variant="body1">{transacao?.present.nome}</Typography>
                  <Chip label={transacao?.present.categoria} size="small" />
                </Box>

                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Valor:
                  </Typography>
                  <Typography variant="h5" color="primary">
                    {transacao && formatarValor(transacao.valor)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Presenteador:
                  </Typography>
                  <Typography variant="body1">{transacao?.presenteadorNome}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {transacao?.presenteadorEmail}
                  </Typography>
                </Box>

                {transacao?.mensagem && (
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      Mensagem:
                    </Typography>
                    <Typography variant="body1" fontStyle="italic">
                      "{transacao.mensagem}"
                    </Typography>
                  </Box>
                )}

                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Gerado em:
                  </Typography>
                  <Typography variant="body2">
                    {transacao && formatarTempo(transacao.createdAt)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2" gutterBottom>
              💳 Quer pagar no crédito? Experimente o PIX no crédito do seu banco — em geral a taxa
              fica perto de 2%. No cartão tradicional, as taxas passam de 5%. Se preferir, compre o
              produto nos nossos parceiros e entregue pessoalmente.
            </Typography>
          </Alert>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              💡 <strong>Instruções:</strong>
              <br />• Abra o app do seu banco
              <br />• Vá em PIX e escaneie o QR Code (o app já abre com o pagamento preenchido) ou
              copie e cole o código
              <br />• Confirme o pagamento de {transacao && formatarValor(transacao.valor)}
              <br />• Pronto! Seu presente será enviado 100% seguro para o dono da lista, sem
              descontos ou taxas.
              <br />
              <strong>
                • Alguns aplicativos falham ao ler o QR Code diretamente pelo app da câmera
              </strong>
            </Typography>
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default PaymentPage;
